import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import LoginRequiredDialog from "@/components/LoginRequiredDialog";
import { useLoginGuard } from "@/hooks/useLoginGuard";
import { toast } from "sonner";
import { ExternalLink, Share2, Gift, ShoppingBag, Check, CheckCircle2, ArrowLeft } from "lucide-react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PriceCompare } from "@/components/site/PriceCompare";
import { Gallery, Reviews, Stars } from "@/components/site/ProductExtras";
import { inr, mvPrice } from "@/lib/compare";
import { PriceAlertButton } from "@/components/site/PriceAlertButton";

const CashbackBox = ({ product }) => {
  const price = mvPrice(product);
  const cb = Math.round((price * product.cashback_percent) / 100);
  return (
    <div className="rounded-3xl bg-primary-700 text-white p-5 flex items-center gap-4" data-testid="product-cashback-box">
      <span className="w-12 h-12 rounded-2xl bg-secondary-500 flex items-center justify-center shrink-0"><Gift className="w-6 h-6 text-primary-900" /></span>
      <div className="flex-1">
        <p className="text-sm text-primary-200">You earn {product.cashback_percent}% cashback</p>
        <p className="font-heading text-2xl font-extrabold">+ {inr(cb)}</p>
      </div>
      <div className="text-right">
        <p className="text-xs text-primary-200">Effective price</p>
        <p className="font-extrabold text-secondary-400 text-xl" data-testid="product-effective-price">{inr(price - cb)}</p>
      </div>
    </div>
  );
};

const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const { requireLogin, dialogOpen, setDialogOpen, dialogMessage } = useLoginGuard();
  const [product, setProduct] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/products/affiliate/${productId}`)
      .then((res) => setProduct(res.data))
      .catch(() => {
        toast.error("Product not found");
        navigate("/products");
      });
  }, [productId, navigate]);

  const shopNow = async () => {
    if (!user) return requireLogin(null, "Please login to earn cashback on this product.");
    try {
      const res = await axios.post(`${API}/products/affiliate/${productId}/click`, {}, { headers: getAuthHeaders() });
      toast.success("Cashback tracking on! Opening store...");
      setTimeout(() => window.open(res.data.redirect_url, "_blank"), 800);
    } catch {
      toast.error("Failed to track click");
    }
  };

  const share = () => {
    if (!user) return requireLogin(null, "Please login to get your unique referral link and earn rewards.");
    navigator.clipboard.writeText(`${window.location.origin}/product/${productId}?ref=${user.referral_code}`);
    setCopied(true);
    toast.success("Link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  if (!product) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-200 border-t-primary-600" /></div>;
  }

  const price = mvPrice(product);
  return (
    <div className="min-h-screen bg-background" data-testid="product-detail-page">
      <LoginRequiredDialog open={dialogOpen} onOpenChange={setDialogOpen} message={dialogMessage} />
      <SiteHeader />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10 space-y-8">
        <button type="button" onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm font-bold text-primary-600" data-testid="product-back-btn"><ArrowLeft className="w-4 h-4" />Back</button>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          <Gallery product={product} />
          <div className="space-y-5">
            <div>
              <span className="inline-block text-xs font-extrabold uppercase tracking-wider bg-primary-100 text-primary-700 rounded-full px-3 py-1">{product.brand_name}</span>
              <h1 className="font-heading text-2xl sm:text-4xl font-extrabold text-primary-900 tracking-tight mt-3" data-testid="product-title">{product.title}</h1>
              <div className="mt-3"><PriceAlertButton productId={productId} requireLogin={requireLogin} /></div>
              {product.rating ? (
                <p className="mt-2 flex items-center gap-2 text-sm text-primary-600"><Stars value={product.rating} />{product.rating}{product.review_count ? ` · ${product.review_count.toLocaleString("en-IN")} ratings` : ""}</p>
              ) : null}
            </div>
            <div className="flex items-baseline gap-3">
              <span className="font-heading text-4xl font-extrabold text-primary-900" data-testid="product-price">{inr(price)}</span>
              {product.discounted_price && <span className="text-lg text-primary-300 line-through">{inr(product.original_price)}</span>}
            </div>
            <CashbackBox product={product} />
            <PriceCompare product={product} onBuyMV={shopNow} />
            <div className="grid sm:grid-cols-2 gap-3">
              <button type="button" onClick={shopNow} className="h-14 rounded-full bg-secondary-500 hover:bg-secondary-400 text-primary-900 font-extrabold inline-flex items-center justify-center gap-2 shadow-[0_5px_0_#B45309] active:translate-y-1 active:shadow-none transition-transform" data-testid="shop-now-btn">
                <ShoppingBag className="w-5 h-5" />Shop & earn<ExternalLink className="w-4 h-4" />
              </button>
              <button type="button" onClick={share} className="h-14 rounded-full border-2 border-primary-600 text-primary-700 font-bold inline-flex items-center justify-center gap-2 hover:bg-primary-50" data-testid="share-btn">
                {copied ? <><Check className="w-5 h-5" />Copied!</> : <><Share2 className="w-5 h-5" />Share & earn {product.commission_percent}%</>}
              </button>
            </div>
            {(product.highlights || []).length > 0 && (
              <ul className="space-y-2" data-testid="product-highlights">
                {product.highlights.map((h) => <li key={h} className="flex gap-2 text-sm text-primary-800"><CheckCircle2 className="w-4 h-4 text-primary-500 shrink-0 mt-0.5" />{h}</li>)}
              </ul>
            )}
            <div>
              <h2 className="font-heading text-lg font-bold text-primary-900 mb-2">About this product</h2>
              <p className="text-sm sm:text-base text-primary-700 leading-relaxed whitespace-pre-line" data-testid="product-description">{product.description}</p>
            </div>
          </div>
        </div>
        <Reviews product={product} />
      </div>
      <SiteFooter />
    </div>
  );
};

export default ProductDetailPage;
